/**
 * Circuit breaker pattern for Stockfish engine to handle failures gracefully
 */

interface CircuitBreakerState {
  isOpen: boolean;
  failureCount: number;
  lastFailureTime: number;
  successCount: number;
}

export class EngineCircuitBreaker {
  private static instance: EngineCircuitBreaker;
  private state: CircuitBreakerState;
  
  private readonly FAILURE_THRESHOLD = 1; // Trigger after just 1 failure for faster fallback
  private readonly TIMEOUT_MS = 5000; // 5 second timeout
  private readonly HALF_OPEN_RETRY_TIMEOUT = 15000; // 15s before retry

  private constructor() {
    this.state = {
      isOpen: false,
      failureCount: 0,
      lastFailureTime: 0,
      successCount: 0
    };
  }

  static getInstance(): EngineCircuitBreaker {
    if (!this.instance) {
      this.instance = new EngineCircuitBreaker();
    }
    return this.instance;
  }

  async executeWithBreaker<T>(operation: () => Promise<T>): Promise<T> {
    if (this.shouldRejectRequest()) {
      console.warn(`[Circuit Breaker] OPEN - rejecting request (failures: ${this.state.failureCount})`);
      throw new Error('Circuit breaker is OPEN - engine temporarily unavailable');
    }

    try {
      console.log(`[Circuit Breaker] Executing operation (state: ${this.state.isOpen ? 'OPEN' : 'CLOSED'})`);
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      console.error('[Circuit Breaker] Operation failed:', error);
      this.onFailure();
      throw error;
    }
  }

  private shouldRejectRequest(): boolean {
    if (!this.state.isOpen) {
      return false;
    }

    // Check if we should attempt a half-open state
    const timeSinceLastFailure = Date.now() - this.state.lastFailureTime;
    if (timeSinceLastFailure > this.HALF_OPEN_RETRY_TIMEOUT) {
      this.state.isOpen = false; // Move to half-open state
      return false;
    }

    return true;
  }

  private onSuccess(): void {
    console.log('[Circuit Breaker] Operation succeeded, resetting failure count');
    this.state.failureCount = 0;
    this.state.successCount++;
    this.state.isOpen = false;
  }

  private onFailure(): void {
    this.state.failureCount++;
    this.state.lastFailureTime = Date.now();
    
    console.warn(`[Circuit Breaker] Failure recorded (${this.state.failureCount}/${this.FAILURE_THRESHOLD})`);
    
    if (this.state.failureCount >= this.FAILURE_THRESHOLD) {
      console.error(`[Circuit Breaker] OPENING circuit after ${this.state.failureCount} failures`);
      this.state.isOpen = true;
    }
  }

  getState(): Readonly<CircuitBreakerState> {
    return { ...this.state };
  }

  reset(): void {
    this.state = {
      isOpen: false,
      failureCount: 0,
      lastFailureTime: 0,
      successCount: 0
    };
  }

  isAvailable(): boolean {
    return !this.shouldRejectRequest();
  }
}

/**
 * Convenience wrapper for engine operations with circuit breaker
 */
export async function withCircuitBreaker<T>(operation: () => Promise<T>): Promise<T> {
  const breaker = EngineCircuitBreaker.getInstance();
  return breaker.executeWithBreaker(operation);
}

/**
 * Check if engine is available (circuit breaker not open)
 */
export function isEngineAvailable(): boolean {
  const breaker = EngineCircuitBreaker.getInstance();
  return breaker.isAvailable();
}